import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { CloseIcon } from "@icarius-icons";
import { Dialog, DialogTitle, DialogContent, Typography, TextField, FormControlLabel, Switch } from "@material-ui/core";
import { getLocalizedString } from "@icarius-localization/strings";
import DialogTitleDivider from "@icarius-common/dialogTitleDivider";
import PaperDraggable from "@icarius-common/paperDraggable";
import ButtonDialogAction from "@icarius-common/buttonDialogAction";
import { createGroupAction, editGroupAction } from "@icarius-pages/groups/actions";
import { openSnackbarAction } from "@icarius-common/snackbar/actions";
import GalleryDialog from "@icarius-common/galleryDialog/components/index";
import { IMAGES_ENDPOINT } from "@icarius-connection/endpoints";
import DialogTransition from "@icarius-common/dialogTransition";
import UsersImageArray from "../../usersImageArray";
import useHandleForm from "./useHandleForm";
import { getUserData } from "@icarius-app/selectors";
import TagsInput from "./tagsInput";
import TagList from "../../tagList";
import SelectionDialog from "@icarius-common/selectionDialog";
import { Alert } from "@material-ui/lab";
import Counter from "@icarius-common/counter";

const FormDialog = (props) => {
    const { open, data, employees, tags, botAssistantEnabled, handleClose } = props;

    const dispatch = useDispatch();
    const userData = useSelector(getUserData);

    const [userSelectionDialogIsOpen, setUserSelectionDialogIsOpen] = useState(false);
    const [imagesDialogIsOpen, setImagesDialogIsOpen] = useState(false);

    const create = (dataToSend) => {
        dispatch(createGroupAction(dataToSend)).then(() => {
            handleClose();
        });
    };

    const modify = (dataToSend) => {
        dispatch(
            editGroupAction({
                ...dataToSend,
                id: data.id,
            })
        ).then(() => {
            handleClose();
        });
    };

    const openValidationError = () => {
        dispatch(openSnackbarAction({ msg: getLocalizedString("invalidData"), severity: "error", duration: 10000 }));
    };

    const openValidationUsersError = () => {
        dispatch(openSnackbarAction({ msg: getLocalizedString("groupAtLeastOneUser"), severity: "error" }));
    };

    const { isCreate, formData, setFormValue, submit } = useHandleForm(
        data,
        create,
        modify,
        openValidationError,
        openValidationUsersError,
        userData.code
    );

    const handleSelectImage = (image) => {
        setFormValue(image, "img");
    };

    const participantsObjects = employees.filter((item) => formData.participants.includes(item.key));

    const handleDeleteTag = (tagKey) => {
        const filteredTags = formData.tags.filter((item) => item !== tagKey);
        setFormValue(filteredTags, "tags");
    };

    return (
        <>
            <Dialog
                TransitionComponent={DialogTransition}
                PaperComponent={PaperDraggable}
                open={open}
                fullWidth
                maxWidth={"sm"}
            >
                <DialogTitle style={{ cursor: "move" }} id="draggable-dialog-title">
                    {getLocalizedString(!isCreate ? "groupEditTitle" : "groupCreateTitle")}
                    <DialogTitleDivider />
                </DialogTitle>
                <CloseIcon className={"dialog-close-icon icon"} onClick={handleClose} />
                <DialogContent>
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 15 }}>
                        <TextField
                            required
                            fullWidth
                            margin={"none"}
                            label={getLocalizedString("groupName")}
                            value={formData.name}
                            onChange={(e) => setFormValue(e.target.value, "name")}
                            inputProps={{ maxLength: 30 }}
                        />
                        <TextField
                            fullWidth
                            margin={"none"}
                            label={getLocalizedString("groupDescription")}
                            value={formData.description}
                            onChange={(e) => setFormValue(e.target.value, "description")}
                            inputProps={{ maxLength: 60 }}
                        />
                        <div
                            onClick={() => setImagesDialogIsOpen(true)}
                            style={{
                                width: "100%",
                                cursor: "pointer",
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: 5,
                            }}
                        >
                            <Typography className="whiteText" style={{ textDecoration: "underline" }}>
                                {getLocalizedString("groupImagesTitle")}
                            </Typography>
                            {formData.img && (
                                <img
                                    style={{ height: 140 }}
                                    src={IMAGES_ENDPOINT + formData.img}
                                    alt={"imageNotFound"}
                                />
                            )}
                        </div>
                        <div style={{ width: "100%" }}>
                            <div style={{ width: "100%", display: "flex", gap: 30 }}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={formData.warehouse}
                                            value={formData.warehouse}
                                            onChange={() => setFormValue(!formData.warehouse, "warehouse")}
                                        />
                                    }
                                    label={getLocalizedString("hasDigitalWarehouse")}
                                    labelPlacement="start"
                                    style={{ marginLeft: 0 }}
                                />
                                <FormControlLabel
                                    disabled={!formData.warehouse || !botAssistantEnabled}
                                    control={
                                        <Switch
                                            checked={formData.hasBotAssistant}
                                            value={formData.hasBotAssistant}
                                            onChange={() => setFormValue(!formData.hasBotAssistant, "hasBotAssistant")}
                                        />
                                    }
                                    label={getLocalizedString("enableGroupChatbot")}
                                    labelPlacement="start"
                                    style={{ marginLeft: 0 }}
                                />
                            </div>
                            {!botAssistantEnabled && (
                                <Alert severity="warning" style={{ width: "100%" }}>
                                    {getLocalizedString("groupChatbotDisabledGeneralParameters")}
                                </Alert>
                            )}
                        </div>
                        <div
                            onClick={() => setUserSelectionDialogIsOpen(true)}
                            style={{ cursor: "pointer", width: "100%" }}
                        >
                            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                <Typography
                                    className="whiteText"
                                    style={{ cursor: "pointer", textDecoration: "underline" }}
                                >
                                    {getLocalizedString("groupAddMembers")}
                                </Typography>
                                <Counter value={formData.participants.length} />
                            </div>
                            <UsersImageArray users={participantsObjects} employees={employees} />
                        </div>
                        {Boolean(tags?.length) && (
                            <div style={{ display: "flex", gap: 8 }}>
                                <TagList tags={tags} values={formData.tags} handleDelete={handleDeleteTag} />
                            </div>
                        )}
                    </div>
                </DialogContent>
                <div
                    style={{
                        display: "flex",
                        padding: 8,
                        alignItems: "center",
                        justifyContent: "space-between",
                    }}
                >
                    <TagsInput tags={tags} values={formData.tags} onChange={(value) => setFormValue(value, "tags")} />
                    <div style={{ display: "flex", gap: 8 }}>
                        <ButtonDialogAction onClick={handleClose} text={getLocalizedString("disagree")} />
                        <ButtonDialogAction
                            isAccept
                            onClick={submit}
                            text={getLocalizedString(!isCreate ? "save" : "create")}
                        />
                    </div>
                </div>
            </Dialog>
            {imagesDialogIsOpen && (
                <GalleryDialog
                    open={imagesDialogIsOpen}
                    initialValue={formData.img}
                    type={"groups"}
                    handleSubmit={(img) => handleSelectImage(img)}
                    handleClose={() => setImagesDialogIsOpen(false)}
                />
            )}
            {userSelectionDialogIsOpen && (
                <SelectionDialog
                    open={userSelectionDialogIsOpen}
                    initialData={formData.participants}
                    items={employees.map((item) => ({ ...item, value: item.name }))}
                    disabledItems={[userData.code]}
                    getItemAvatar={(item) => item["FOTOGRAFIA"]}
                    title={getLocalizedString("addMembers")}
                    selectedTitle={getLocalizedString("membersSelected")}
                    handleClose={() => setUserSelectionDialogIsOpen(false)}
                    handleSubmit={(data) => setFormValue(data, "participants")}
                />
            )}
        </>
    );
};

export default FormDialog;
